import "./analytics";
import "./contact";

// Native disclosure remains usable even when JavaScript is unavailable.
const menu = document.querySelector(".bm-mobile-menu");
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const supportsAnimation = typeof Element.prototype.animate === "function";

// Tabs follow the WAI-ARIA pattern. The tab list stays hidden and every panel
// stays visible until JavaScript runs.
const setupTabs = (tablist, onSelect) => {
  const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
  const panels = tabs.map((tab) =>
    document.getElementById(tab.getAttribute("aria-controls")),
  );

  const selectTab = (selectedIndex) => {
    if (tabs[selectedIndex].getAttribute("aria-selected") === "true") return;
    tabs.forEach((tab, index) => {
      const selected = index === selectedIndex;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
      panels[index].hidden = !selected;
    });
    onSelect?.(panels[selectedIndex]);
  };

  tabs.forEach((tab, index) => {
    panels[index].hidden = tab.getAttribute("aria-selected") !== "true";
    tab.addEventListener("click", () => selectTab(index));
    tab.addEventListener("keydown", (event) => {
      let nextIndex;
      switch (event.key) {
        case "ArrowRight":
          nextIndex = (index + 1) % tabs.length;
          break;
        case "ArrowLeft":
          nextIndex = (index - 1 + tabs.length) % tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      selectTab(nextIndex);
      tabs[nextIndex].focus();
    });
  });

  tablist.hidden = false;
};

const preview = document.querySelector("[data-product-preview]");

if (preview) {
  let screenshotFade;

  preview.addEventListener("focusin", (event) => {
    if (event.target.closest(".bm-preview-window")) screenshotFade?.cancel();
  });
  reducedMotion.addEventListener("change", (event) => {
    if (event.matches) screenshotFade?.cancel();
  });

  setupTabs(preview.querySelector('[role="tablist"]'), (panel) => {
    screenshotFade?.cancel();
    if (!reducedMotion.matches && supportsAnimation) {
      screenshotFade = panel
        .querySelector(".bm-preview-window")
        .animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 200,
          easing: "ease-out",
        });
    }
  });
}

document
  .querySelectorAll("[data-tabs]")
  .forEach((root) => setupTabs(root.querySelector('[role="tablist"]')));

// Carousels scroll natively (touch, trackpad, keyboard on the focused track).
// The arrows appear only when JavaScript runs and there is more to scroll.
document.querySelectorAll("[data-carousel]").forEach((carousel) => {
  const track = carousel.querySelector("[data-carousel-track]");
  const prev = carousel.querySelector("[data-carousel-prev]");
  const next = carousel.querySelector("[data-carousel-next]");

  const update = () => {
    const maxScroll = track.scrollWidth - track.clientWidth;
    const focused = document.activeElement;
    prev.hidden = track.scrollLeft <= 1;
    next.hidden = track.scrollLeft >= maxScroll - 1;
    // Keep keyboard focus on an arrow when the focused one disappears.
    if (focused === prev && prev.hidden) next.focus();
    if (focused === next && next.hidden) prev.focus();
  };

  const scrollByCard = (direction) => {
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({
      left: direction * (track.firstElementChild.offsetWidth + gap),
      behavior: reducedMotion.matches ? "auto" : "smooth",
    });
  };

  prev.addEventListener("click", () => scrollByCard(-1));
  next.addEventListener("click", () => scrollByCard(1));
  track.addEventListener("scroll", update, { passive: true });
  new ResizeObserver(update).observe(track);
  update();
});

if (menu) {
  const trigger = menu.querySelector("summary");
  menu.addEventListener("click", (event) => {
    if (event.target.closest("a")) menu.open = false;
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && menu.open) {
      menu.open = false;
      trigger.focus();
    }
  });
  document.addEventListener("click", (event) => {
    if (!menu.contains(event.target)) menu.open = false;
  });
  window
    .matchMedia("(min-width: 801px)")
    .addEventListener("change", (event) => {
      if (event.matches) menu.open = false;
    });
}

// Content stays visible without JavaScript or animation support. Animate just
// before it enters the viewport, once per visit, without changing page layout.
const homepage = document.querySelector(".bm-home");

if (
  homepage &&
  !reducedMotion.matches &&
  "IntersectionObserver" in window &&
  supportsAnimation
) {
  const targets = new Map();
  homepage
    .querySelectorAll(
      ".bm-section-heading, .bm-spotlight-grid > div, .bm-about-inner, .bm-contact-panel",
    )
    .forEach((element) => targets.set(element, { delay: 0, rise: 16 }));
  [
    [".bm-hero-copy .bm-eyebrow", 0, 0],
    [".bm-hero-copy h1", 80, 12],
    [".bm-hero-copy .bm-lead", 160, 12],
    [".bm-hero-copy .bm-actions", 240, 12],
    [".bm-hero-copy .bm-hero-note", 280, 0],
    [".bm-product-preview", 160, 0],
  ].forEach(([selector, delay, rise]) => {
    const element = homepage.querySelector(selector);
    if (element) targets.set(element, { delay, rise });
  });
  homepage
    .querySelectorAll(
      ".bm-product-grid, .bm-services-grid, .bm-workflow, .doh-steps, .doh-parts",
    )
    .forEach((group) => {
      Array.from(group.children).forEach((element, index) => {
        targets.set(element, { delay: Math.min(index, 2) * 80, rise: 16 });
      });
    });

  const running = new Map();
  const observer = new IntersectionObserver(
    (entries) => {
      if (reducedMotion.matches) return;
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        // Never animate a control that a keyboard user is interacting with.
        if (target.contains(document.activeElement)) return;
        const { delay, rise } = targets.get(target);
        const animation = target.animate(
          [
            { opacity: 0, transform: `translateY(${rise}px)` },
            { opacity: 1, transform: "translateY(0)" },
          ],
          {
            duration: 480,
            delay,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            fill: "backwards",
          },
        );
        running.set(target, animation);
        animation.onfinish = animation.oncancel = () => running.delete(target);
      });
    },
    { rootMargin: "0px 0px 32px 0px", threshold: 0 },
  );
  targets.forEach((_, element) => observer.observe(element));

  homepage.addEventListener("focusin", (event) => {
    targets.forEach((_, element) => {
      if (!element.contains(event.target)) return;
      observer.unobserve(element);
      running.get(element)?.cancel();
    });
  });

  reducedMotion.addEventListener("change", (event) => {
    if (!event.matches) return;
    observer.disconnect();
    running.forEach((animation) => animation.cancel());
    running.clear();
  });
}
